<script>
export default {
  name: "DidoResourcePaginatedDatafile",
};
</script>

<script setup>
import { computed, inject } from "vue";
import {
  displayDateFormatDDMMYYYY,
  displayMillesimesNumber,
} from "../../../utils";
import DidoTextTruncate from "../Truncate.vue";
import DidoDownloadButtonMillesime from "../../download/buttons/Millesime.vue";

// inject variable to know if we are in catalog
const inCatalog = inject("inCatalog", null);

// inject method to create a catalogUrl
const catalogUrl = inject("catalogUrl", () => null);

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  datafile: { type: Object, required: true },
});

// define events emitted
const emit = defineEmits(["selected"]);

// computed data for the specs of QCard
const tagQCard = computed(() => (inCatalog ? "a" : "div"));
const hrefQCard = computed(() =>
  inCatalog
    ? catalogUrl.value({ type: "datafile", datafileRid: props.datafile.rid })
    : null,
);
const titleQCard = computed(() =>
  inCatalog
    ? `Voir le détail du fichier de données ${props.datafile.title}`
    : null,
);
const hasClickEventQCard = computed(() => (inCatalog ? "click" : null));
const clickQCard = computed(() => () => emit("selected"));

// computed data for modification date
const modificationDate = computed(() => {
  const dateToDisplay =
    props.datafile.last_modified || props.datafile.created_at;
  return displayDateFormatDDMMYYYY(dateToDisplay);
});
// computed data for publication date
const publicationDate = computed(() =>
  displayDateFormatDDMMYYYY(props.datafile.published),
);
</script>

<template>
  <QCard
    :tag="tagQCard"
    :href="hrefQCard"
    :title="titleQCard"
    bordered
    square
    flat
    class="dido-dataset-resource"
    @[hasClickEventQCard].prevent="clickQCard"
  >
    <div class="dido-dataset-resource-title">{{ props.datafile.title }}</div>
    <!-- Timestamps -->
    <div
      :class="`q-my-sm row-start-center q-col-gutter-${
        $q.screen.gt.xs ? 'sm' : 'xs'
      } text-small`"
    >
      <!-- Modification Date -->
      <span class="col-12 col-sm-auto">
        Modifié le {{ modificationDate }}
      </span>
      <!-- Separator -->
      <span v-if="$q.screen.gt.xs" class="col-0 col-sm-auto">|</span>
      <!-- Publication Date -->
      <span class="col-12 col-sm-auto"> Publié le {{ publicationDate }} </span>
    </div>
    <!-- Description -->
    <div class="text-justify">
      <DidoTextTruncate
        :text="props.datafile.description"
        :length="200"
        class="text-justify"
        :link-classes="[
          'cursor-pointer',
          'on-right',
          'text-primary',
          'text-underline',
          'text-small',
        ]"
      />
    </div>
    <!-- Donwload and Millesimes Counters -->
    <div
      :class="`col-12 q-mt-sm row-start-center q-col-gutter-${
        $q.screen.gt.xs ? 'sm' : 'xs'
      } text-small`"
    >
      <!-- Millesimes Counter -->
      <span class="col-12 col-md-auto">
        {{ displayMillesimesNumber(props.datafile.millesimes.length) }}
      </span>
      <!-- Separator -->
      <span v-if="$q.screen.gt.sm" class="col-auto">|</span>
      <!-- Download Counter -->
      <span class="col-12 col-sm-auto">
        Téléchargé {{ props.datafile.downloads }} fois
      </span>
      <!-- Separator -->
      <span v-if="$q.screen.gt.xs" class="col-auto">|</span>
      <!-- Download Link -->
      <span class="col-12 col-sm-6 col-md-auto">
        <DidoDownloadButtonMillesime
          :api-url="props.apiUrl"
          :millesime-object="{
            title: props.datafile.title,
            rid: props.datafile.rid,
            millesime: props.datafile.millesimes[0].millesime,
            geoFields: props.datafile.millesimes[0].geoFields,
          }"
          @click.stop.prevent
        />
      </span>
    </div>
  </QCard>
</template>
